import { markdownToSafeHtml } from '../lib/markdown';
import {
  expandWikiRefsToMarkdown,
  WikiRefProject,
  wikiUrl,
} from '../lib/wiki-refs';
import { expandCardRefsToMarkdown } from '../lib/card-refs';
import type { CardRef } from '../client';
import { rewriteChartEmbeds } from '../databases/charts/chart-embeds';

/**
 * Wiki-flavoured markdown: resolves [[wikilinks]], rewrites relative and
 * drive: embeds to the wiki asset endpoint, and upgrades video embeds to a
 * player — all as TEXT transforms before markdownToSafeHtml, so DOMPurify
 * sanitization applies to everything the page emits.
 */

export interface WikiRenderContext {
  teamId: string | number;
  /**
   * Project bare [[path]] links resolve against. Absent on surfaces with
   * no single project scope (e.g. team chat), where only cross-project
   * [[KEY:path]] links can resolve.
   */
  projectId?: number;
  /**
   * Path of the page being rendered (wiki-root-relative, no .md). Empty
   * when there is no current page (e.g. rendering a comment/description),
   * which disables ./ and ../ relative wikilink resolution.
   */
  pagePath: string;
  /** Known projects for cross-project [[KEY:path]] / drive:KEY:path. */
  projects?: WikiRefProject[];
  /**
   * Team card refs for #KEY-N expansion. Wiki pages link cards with the
   * same JIRA-style tokens the rest of the app uses.
   */
  cardRefs?: CardRef[];
  /**
   * Paths of pages that exist in THIS wiki (incl. archived). When
   * provided, same-project wikilinks to missing pages render as "red
   * links" — still clickable (the target offers to create the page).
   */
  knownPaths?: Set<string>;
}

const VIDEO_EXTENSIONS = /\.(mp4|webm|mov|m4v)$/i;
const AUDIO_EXTENSIONS = /\.(mp3|wav|m4a|ogg)$/i;

export function wikiAssetUrl(
  teamId: string | number,
  projectId: number,
  assetPath: string,
  fromDriveRoot: boolean,
): string {
  const params = new URLSearchParams({ path: assetPath });
  if (fromDriveRoot) params.set('drive', 'true');
  return `/api/teams/${teamId}/projects/${projectId}/wiki/asset?${params.toString()}`;
}

/** Folder of the current page, '' at the wiki root. */
function pageFolder(pagePath: string): string {
  const idx = pagePath.lastIndexOf('/');
  return idx === -1 ? '' : pagePath.slice(0, idx);
}

/**
 * Resolve `./`, `../`, and bare relative paths against a base folder,
 * never escaping the root ('' return = escape attempt, caller drops it).
 */
export function resolveRelativePath(base: string, relative: string): string {
  const segments = relative.startsWith('/')
    ? relative.slice(1).split('/')
    : [...(base ? base.split('/') : []), ...relative.split('/')];
  const resolved: string[] = [];
  for (const segment of segments) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') {
      if (resolved.length === 0) return '';
      resolved.pop();
      continue;
    }
    resolved.push(segment);
  }
  return resolved.join('/');
}

function hasScheme(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(url);
}

/** drive:path or drive:KEY:path → {projectId, path} for the viewer. */
function resolveDriveTarget(
  target: string,
  ctx: WikiRenderContext,
): { projectId: number; path: string } | null {
  const rest = target.slice('drive:'.length);
  const crossProject = /^([A-Za-z][A-Za-z0-9]{0,9}):(.+)$/.exec(rest);
  if (crossProject) {
    const key = crossProject[1].toUpperCase();
    const project = (ctx.projects || []).find(
      (candidate) => candidate.key.toUpperCase() === key,
    );
    if (!project) return null;
    return { projectId: project.projectId, path: crossProject[2] };
  }
  if (ctx.projectId === undefined) return null;
  return { projectId: ctx.projectId, path: rest };
}

/** [[path]], [[path|label]], [[KEY:path]], [[KEY:path|label]] → md links.
 *  Code fences and inline code spans are matched first and left untouched,
 *  so a literal \`[[example]]\` renders as written. */
export function expandWikilinks(
  markdown: string,
  ctx: WikiRenderContext,
): string {
  return markdown.replace(
    /```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`|\[\[([^\[\]|]+)(?:\|([^\[\]|]*))?\]\]/g,
    (token, rawTarget?: string, rawLabel?: string) => {
      if (rawTarget === undefined) return token; // inside code: leave as-is
      // Agents escape the wikilink pipe (`[[a/b\|Label]]`) so it survives a
      // markdown table cell; the regex then captures that backslash into the
      // target, and encodeURIComponent turns it into a stray `%5C` on the
      // href. Strip trailing backslashes so the path resolves cleanly.
      const target = rawTarget.trim().replace(/\\+$/, '');
      if (!target) return token;
      const cross = /^([A-Za-z][A-Za-z0-9]{0,9}):(.+)$/.exec(target);
      let projectId = ctx.projectId;
      let path = target;
      if (cross) {
        const project = (ctx.projects || []).find(
          (candidate) => candidate.key.toUpperCase() === cross[1].toUpperCase(),
        );
        if (!project) return token; // unknown project: leave as text
        projectId = project.projectId;
        path = cross[2].trim();
      }
      if (projectId === undefined) return token;
      if (path.startsWith('./') || path.startsWith('../')) {
        path = resolveRelativePath(pageFolder(ctx.pagePath), path);
        if (!path) return token;
      }
      const label = (rawLabel ?? '').trim() || path;
      const url = wikiUrl(ctx.teamId, projectId, path);
      // Same-project link to a page that doesn't exist: classic red link.
      // Emitted as raw HTML (markdown links can't carry a class); it still
      // flows through DOMPurify with everything else.
      if (!cross && ctx.knownPaths && !ctx.knownPaths.has(path)) {
        return `<a href="${url}" class="wiki-broken-link" title="This page does not exist yet — click to create it">${escapeHtml(label)}</a>`;
      }
      return `[${label}](${url})`;
    },
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Rewrite markdown link/image targets that point at wiki-relative files or
 * drive: URIs to the asset endpoint. Video/audio image-embeds become
 * players (DOMPurify keeps <video>/<audio> and strips anything hostile).
 */
function rewriteEmbeds(markdown: string, ctx: WikiRenderContext): string {
  return markdown.replace(
    /(!?)\[([^\]]*)\]\(([^)\s]+)\)/g,
    (token, bang: string, label: string, target: string) => {
      let assetUrlValue: string | null = null;
      if (target.startsWith('drive:')) {
        const resolved = resolveDriveTarget(target, ctx);
        if (!resolved) return `${bang ? '' : ''}${label || target}`;
        assetUrlValue = wikiAssetUrl(
          ctx.teamId,
          resolved.projectId,
          resolved.path,
          true,
        );
      } else if (
        bang &&
        !hasScheme(target) &&
        !target.startsWith('/') &&
        ctx.projectId !== undefined
      ) {
        // Wiki-file embed. Bare paths are wiki-ROOT-relative (matching
        // wikilinks); only ./ and ../ resolve against the page's folder.
        const resolved =
          target.startsWith('./') || target.startsWith('../')
            ? resolveRelativePath(pageFolder(ctx.pagePath), target)
            : resolveRelativePath('', target);
        if (!resolved) return token;
        assetUrlValue = wikiAssetUrl(
          ctx.teamId,
          ctx.projectId,
          resolved,
          false,
        );
      }
      if (!assetUrlValue) return token;

      if (bang) {
        const fileName = target.split('/').pop() || target;
        if (VIDEO_EXTENSIONS.test(fileName)) {
          return `<video controls src="${assetUrlValue}" title="${escapeAttr(label)}" style="max-width:100%"></video>`;
        }
        if (AUDIO_EXTENSIONS.test(fileName)) {
          return `<audio controls src="${assetUrlValue}" title="${escapeAttr(label)}"></audio>`;
        }
        return `![${label}](${assetUrlValue})`;
      }
      return `[${label}](${assetUrlValue})`;
    },
  );
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Pages conventionally open with an `# H1` — the same heading the server
 * derives the page title from, which the page header already displays.
 * Drop it from the rendered view so the title doesn't appear twice.
 */
function stripLeadingH1(body: string): string {
  const lines = body.split('\n');
  const first = lines.findIndex((line) => line.trim() !== '');
  if (first !== -1 && /^# /.test(lines[first].trim())) {
    return lines.slice(first + 1).join('\n');
  }
  return body;
}

export function renderWikiMarkdown(
  body: string,
  ctx: WikiRenderContext,
): string {
  // #KEY-N card refs and wiki:KEY/path short refs link the same way they
  // do in comments and chat.
  const withCardRefs = expandCardRefsToMarkdown(
    stripLeadingH1(body),
    ctx.cardRefs,
    ctx.teamId,
  );
  const withWikiRefs = expandWikiRefsToMarkdown(
    withCardRefs,
    ctx.projects,
    ctx.teamId,
  );
  const withWikilinks = expandWikilinks(withWikiRefs, ctx);
  // Chart embeds first: their `chart:` targets would otherwise be skipped by
  // rewriteEmbeds' scheme check. Placeholders hydrate into live ChartViews
  // after render (useChartEmbeds in ProjectWiki).
  const withCharts = rewriteChartEmbeds(withWikilinks);
  const withEmbeds = rewriteEmbeds(withCharts, ctx);
  return markdownToSafeHtml(withEmbeds);
}
